/**
 * Global Email Sync Status Indicator
 * Shows a persistent banner when email sync is running
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, X, ChevronDown, ChevronUp } from "lucide-react";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import axios from "axios";
import { API_URL } from "../config/api";

const EmailSyncStatus = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const token = localStorage.getItem("token");

  const checkSyncStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/offer-archive/sync/progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "running" || response.data.status === "in_progress") {
        setSyncStatus(response.data);
        setDismissed(false);
      } else {
        setSyncStatus(null);
      }
    } catch (error) {
      // Not logged in or no access
      setSyncStatus(null);
    }
  }, [token]);

  useEffect(() => {
    // Initial check
    checkSyncStatus();
    
    // Poll every 5 seconds
    const interval = setInterval(checkSyncStatus, 5000);
    
    return () => clearInterval(interval);
  }, [checkSyncStatus]);

  if (!syncStatus || dismissed) return null;

  const progress = syncStatus.progress || {};
  const percentComplete = progress.percent_complete || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="fixed top-16 left-0 right-0 z-50 px-4"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-blue-600 text-white rounded-lg shadow-lg overflow-hidden">
            {/* Compact View */}
            <div className="px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                <Mail className="h-4 w-4" />
                <span className="font-medium text-sm">
                  Syncing emails: {percentComplete}% complete
                </span>
                <span className="text-blue-200 text-sm">
                  ({progress.processed?.toLocaleString() || 0} / {progress.total_to_process?.toLocaleString() || 0})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-blue-700 h-6 px-2"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-blue-700 h-6 px-2"
                  onClick={() => setDismissed(true)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <Progress value={percentComplete} className="h-1 bg-blue-800" />
            
            {/* Expanded Details */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="px-4 py-3 bg-blue-700 text-sm"
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-blue-200">Total in Inbox:</span>
                      <span className="ml-2 font-medium">{progress.total_in_inbox?.toLocaleString() || 0}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">New:</span>
                      <span className="ml-2 font-medium text-green-300">{progress.new_count?.toLocaleString() || 0}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">Skipped:</span>
                      <span className="ml-2 font-medium">{progress.skipped_count?.toLocaleString() || 0}</span>
                    </div>
                    <div>
                      <span className="text-blue-200">Batch:</span>
                      <span className="ml-2 font-medium">{progress.current_batch || 0} / {progress.total_batches || 0}</span>
                    </div>
                  </div>
                  {progress.last_update && (
                    <div className="mt-2 text-blue-200 text-xs">
                      Last update: {new Date(progress.last_update).toLocaleTimeString()}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailSyncStatus;
